import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  MasterDetail,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useMemo, useRef, useState } from 'react';
import {
  GridDetailPopup,
  OPS_GRID_TOOLBAR_DESKTOP_ONLY,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  createRemoteGridFilterOptionChangedHandler,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey,
  type OpsGridToolbarMenuItem
} from '@jarvis/platform-ui';
import { formatLocalDateTime } from '../../../../utils/timezone';
import type { SyncRun, SyncRunListPage, SyncRunStatus } from '../../../../api/syncJobs';

const STATUS_BADGES: Record<SyncRunStatus, { label: string; bg: string; fg: string }> = {
  Running: { label: 'Running', bg: 'var(--ops-status-running-bg)', fg: 'var(--ops-status-running-fg)' },
  Succeeded: { label: 'Succeeded', bg: 'var(--ops-status-succeeded-bg)', fg: 'var(--ops-status-succeeded-fg)' },
  PartialSuccess: { label: 'Partial', bg: 'var(--ops-status-partial-bg)', fg: 'var(--ops-status-partial-fg)' },
  Failed: { label: 'Failed', bg: 'var(--ops-status-failed-bg)', fg: 'var(--ops-status-failed-fg)' },
  Cancelled: { label: 'Cancelled', bg: 'var(--ops-status-cancelled-bg)', fg: 'var(--ops-status-cancelled-fg)' }
};

function StatusBadge({ status }: { status: SyncRunStatus }) {
  const tone = STATUS_BADGES[status] ?? STATUS_BADGES.Running;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: tone.bg,
        color: tone.fg
      }}
    >
      {tone.label}
    </span>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return formatLocalDateTime(value) || '—';
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return 'running';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0 || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

export function DetailsRender({ row }: { row: SyncRun }) {
  const json = row.details;
  return (
    <div style={{ padding: '0.5rem 0.75rem' }}>
      <div style={{ marginBottom: '0.5rem', color: 'var(--ops-color-muted, #6b7280)', fontSize: 12 }}>
        Run id <code>{row.id}</code> · started {formatDate(row.startedAt)} · finished {formatDate(row.finishedAt)} ·
        duration {formatDuration(row.startedAt, row.finishedAt)}
      </div>
      <pre className="ops-grid-detail-mono-block" style={{ maxHeight: 280 }}>
        {json ? JSON.stringify(json, null, 2) : 'No structured details for this run.'}
      </pre>
    </div>
  );
}

export interface SyncRunsGridProps {
  /** Loader the page provides; the grid stays agnostic of admin vs. feature endpoints. */
  loadPage: (request: SyncRunsLoadRequest) => Promise<SyncRunListPage>;
  /** Optional loader for the master-detail row that returns the full run including details. */
  loadDetails?: (id: string) => Promise<SyncRun>;
  /** Whether to show the JobName column (admin: yes; feature: no). */
  showJobName?: boolean;
  /** Default page size for the in-grid pager. */
  pageSize?: number;
  /** Local storage key for persisted column widths/order/filter/search/page size. */
  stateStorageKey: string;
  /** When set, renders a Back to jobs button in the grid toolbar. */
  onBackToJobs?: () => void;
}

export interface SyncRunsLoadRequest {
  skip: number;
  take: number;
  search?: string;
  filter?: string;
  sortBy?: string;
  sortDesc?: boolean;
}

export const SYNC_RUNS_GRID_SCHEMA_VERSION = 1;

export function syncRunsGridColumns(showJobName: boolean): string[] {
  const columns = ['status'];
  if (showJobName) columns.push('jobName');
  columns.push(
    'startedAt',
    'triggerKind',
    'triggeredBy',
    'processed',
    'pushed',
    'deleted',
    'errors',
    'summary'
  );
  return columns;
}

type SortDescriptor = {
  selector?: unknown;
  desc?: boolean;
};

function getFirstSort(sort: unknown): SortDescriptor | null {
  if (!sort) return null;
  if (Array.isArray(sort)) return (sort[0] as SortDescriptor | undefined) ?? null;
  return sort as SortDescriptor;
}

function buildLoadRequest(
  options: {
    skip?: number;
    take?: number;
    searchValue?: unknown;
    filter?: unknown;
    sort?: unknown;
  },
  fallbackTake: number
): SyncRunsLoadRequest {
  const sort = getFirstSort(options.sort);
  const selector = typeof sort?.selector === 'string' ? sort.selector : undefined;
  const search = typeof options.searchValue === 'string' ? options.searchValue.trim() : '';
  return {
    skip: typeof options.skip === 'number' ? options.skip : 0,
    take: typeof options.take === 'number' ? options.take : fallbackTake,
    search: search || undefined,
    filter: options.filter ? JSON.stringify(options.filter) : undefined,
    sortBy: selector,
    sortDesc: sort?.desc
  };
}

async function hydrateRunDetails(
  row: SyncRun,
  loadDetails: ((id: string) => Promise<SyncRun>) | undefined,
  cache: Map<string, SyncRun>
): Promise<SyncRun> {
  const cached = cache.get(row.id);
  if (cached) return cached;
  if (!loadDetails) return row;
  const full = await loadDetails(row.id);
  cache.set(full.id, full);
  return full;
}

export function SyncRunsGrid({
  loadPage,
  loadDetails,
  showJobName = true,
  pageSize = 100,
  stateStorageKey,
  onBackToJobs
}: SyncRunsGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [detailsVersion, setDetailsVersion] = useState(0);
  const [viewRun, setViewRun] = useState<SyncRun | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const detailsCacheRef = useRef<Map<string, SyncRun>>(new Map());

  const gridColumns = useMemo(() => syncRunsGridColumns(showJobName), [showJobName]);

  const dataSource = useMemo(() => {
    const store = new CustomStore<SyncRun, string>({
      key: 'id',
      load: async (options) => {
        const page = await loadPage(buildLoadRequest(options, pageSize));
        return { data: page.items, totalCount: page.total };
      },
      byKey: async (id) => {
        const cached = detailsCacheRef.current.get(String(id));
        if (cached) return cached;
        if (!loadDetails) {
          throw new Error('No details loader configured.');
        }
        const full = await loadDetails(String(id));
        detailsCacheRef.current.set(full.id, full);
        setDetailsVersion((v) => v + 1);
        return full;
      }
    });
    return new DataSource({ store, pageSize, paginate: true });
  }, [loadPage, loadDetails, pageSize]);

  const onRemoteFilterOptionChanged = useMemo(
    () => createRemoteGridFilterOptionChangedHandler(dataSource),
    [dataSource]
  );

  async function openViewPopup(row: SyncRun) {
    setViewRun(row);
    if (!loadDetails || detailsCacheRef.current.has(row.id)) {
      const cached = detailsCacheRef.current.get(row.id);
      if (cached) setViewRun(cached);
      return;
    }
    setViewLoading(true);
    try {
      const full = await hydrateRunDetails(row, loadDetails, detailsCacheRef.current);
      setDetailsVersion((v) => v + 1);
      setViewRun(full);
    } catch {
      /* keep list row data in popup */
    } finally {
      setViewLoading(false);
    }
  }

  const toolbarExtraMenuItems = useMemo((): OpsGridToolbarMenuItem[] => {
    if (!onBackToJobs) return [];
    return [{ id: 'back-to-jobs', text: 'Back to jobs', icon: 'back', onClick: onBackToJobs }];
  }, [onBackToJobs]);

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={dataSource}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        wordWrapEnabled
        remoteOperations={{ paging: true, filtering: true, sorting: true }}
        onOptionChanged={onRemoteFilterOptionChanged}
        onRowDblClick={(e) => onGridRowDblClick(e, (row) => void openViewPopup(row))}
        onKeyDown={(e) => onGridFocusedRowEnter(e, (row) => void openViewPopup(row))}
        onRowExpanding={async (e) => {
          if (!loadDetails) return;
          const key = String(e.key);
          if (detailsCacheRef.current.has(key)) return;
          try {
            const full = await loadDetails(key);
            detailsCacheRef.current.set(full.id, full);
            setDetailsVersion((v) => v + 1);
            gridRef.current?.instance()?.repaintRows([e.component.getRowIndexByKey(e.key)]);
          } catch {
            /* row still expands with list data */
          }
        }}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={SYNC_RUNS_GRID_SCHEMA_VERSION}
          columns={gridColumns}
        />
        <Toolbar>
          {onBackToJobs ? (
            <Item
              location="before"
              cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}
              widget="dxButton"
              options={{
                text: 'Back to jobs',
                icon: 'back',
                stylingMode: 'outlined',
                onClick: onBackToJobs
              }}
            />
          ) : null}
          <OpsGridToolbarItems
            gridRef={gridRef}
            onRefresh={() => {
              detailsCacheRef.current.clear();
              void dataSource.reload();
            }}
            refreshHint="Reload runs"
            extraMenuItems={toolbarExtraMenuItems}
            export={{ fileName: 'sync-runs-export', worksheetName: 'Sync runs', hint: 'Export runs' }}
            resetLayout={{
              onReset: () => {
                detailsCacheRef.current.clear();
                localStorage.removeItem(stateStorageKey);
                gridRef.current?.instance()?.state(null);
              }
            }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <SearchPanel visible width={240} placeholder="Search sync runs…" />
        <HeaderFilter visible />
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={pageSize} />
        <Pager
          visible
          showInfo
          showNavigationButtons
          showPageSizeSelector
          allowedPageSizes={[100, 250, 500, 1000]}
          displayMode="full"
        />
        <Column
          name="status"
          dataField="status"
          caption="Status"
          allowSorting
          width={130}
          cellRender={(c) => <StatusBadge status={c.value as SyncRunStatus} />}
        />
        {showJobName ? <Column name="jobName" dataField="jobName" caption="Job" allowSorting /> : null}
        <Column
          name="startedAt"
          dataField="startedAt"
          caption="Started"
          dataType="datetime"
          calculateDisplayValue={(row: SyncRun) => formatDate(row.startedAt)}
        />
        <Column
          caption="Duration"
          allowFiltering={false}
          allowHeaderFiltering={false}
          calculateCellValue={(row: SyncRun) => formatDuration(row.startedAt, row.finishedAt)}
        />
        <Column name="triggerKind" dataField="triggerKind" caption="Trigger" width={110} />
        <Column name="triggeredBy" dataField="triggeredBy" caption="By" />
        <Column name="processed" dataField="processed" caption="Processed" alignment="right" width={100} />
        <Column name="pushed" dataField="pushed" caption="Pushed" alignment="right" width={90} />
        <Column name="deleted" dataField="deleted" caption="Deleted" alignment="right" width={90} />
        <Column name="errors" dataField="errors" caption="Errors" alignment="right" width={90} />
        <Column name="summary" dataField="summary" caption="Summary" minWidth={220} />
        <MasterDetail
          enabled={Boolean(loadDetails)}
          component={({ data }) => {
            const cached = detailsCacheRef.current.get(data.data.id);
            return <DetailsRender key={`${data.data.id}:${detailsVersion}`} row={cached ?? data.data} />;
          }}
        />
      </DataGrid>

      <GridDetailPopup
        visible={viewRun !== null}
        title="Sync run details"
        onClose={() => setViewRun(null)}
        width={680}
        maxBodyHeight={480}
        sizeStorageKey={opsGridDetailPopupSizeKey(stateStorageKey)}
      >
        {viewRun ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <StatusBadge status={viewRun.status} />
              {viewRun.jobName ? (
                <span style={{ fontSize: 14, color: 'var(--ops-color-muted, #64748b)' }}>{viewRun.jobName}</span>
              ) : null}
            </div>
            {viewLoading ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0' }}>
                <LoadIndicator visible height={18} width={18} />
                <span style={{ fontSize: 13, color: 'var(--ops-color-muted, #64748b)' }}>Loading run details…</span>
              </div>
            ) : (
              <DetailsRender row={viewRun} />
            )}
          </>
        ) : null}
      </GridDetailPopup>
    </>
  );
}
